// ============================================================================
// KLINISOL CRO - Forgot Password Component
// FDA 21 CFR Part 11 Compliant
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import Navbar from './Navbar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (data.success) {
                setSuccess(true);
            } else {
                setError(data.message || 'Failed to process request');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        }

        setLoading(false);
    };

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-gradient-to-br from-[#475569] via-[#E91E63] to-[#475569] flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md">
                    {/* Logo Section */}
                    <div className="text-center mb-8">
                        <h1 className="text-5xl font-bold text-white mb-2">
                            KLIN<span className="text-[#E91E63]">i</span>SOL
                        </h1>
                        <p className="text-white text-xs opacity-75">Advancing Investigator-Initiated Research</p>
                    </div>

                    {/* Card */}
                    <div className="bg-white rounded-2xl shadow-2xl p-8">
                        {success ? (
                            // Success State
                            <div className="text-center">
                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>
                                <p className="text-gray-600 mb-6">
                                    If an account exists with that email, we've sent password reset instructions.
                                </p>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-all"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                    Back to Login
                                </button>
                            </div>
                        ) : (
                            // Form State
                            <>
                                <div className="flex items-center justify-center gap-2 mb-6">
                                    <Mail className="w-6 h-6 text-[#475569]" />
                                    <h2 className="text-2xl font-bold text-gray-900">Forgot Password</h2>
                                </div>

                                <p className="text-gray-600 text-sm mb-6 text-center">
                                    Enter your email address and we'll send you a link to reset your password.
                                </p>

                                {error && (
                                    <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                                        <p className="text-sm font-medium">{error}</p>
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    <div>
                                        <label 
                                            htmlFor="email" 
                                            className="block text-sm font-semibold text-gray-700 mb-2"
                                        >
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            placeholder="Enter your email"
                                            disabled={loading}
                                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all disabled:bg-gray-100"
                                        />
                                    </div>

                                    <button 
                                        type="submit" 
                                        disabled={loading}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-bold rounded-lg hover:from-[#334155] hover:to-[#1e293b] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                                <span>Sending...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Mail className="w-5 h-5" />
                                                <span>Send Reset Link</span>
                                            </>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigate('/login')}
                                        className="w-full flex items-center justify-center gap-2 px-6 py-3 text-[#475569] font-medium hover:text-[#E91E63] transition-colors"
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                        Back to Login
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;
